<script setup lang="ts">
import {queryParams} from "@/store/bt";
import BTCell from "@/bt/BTCell.vue";
import BenefitsHeaderRecursive from "@/bt/BenefitsHeaderRecursive.vue";
import {computed, onBeforeMount, ref, watch} from "vue";
import {BTNode} from "#/BTNode";
import {useFetch} from "@/composables/useFetch";
import SpinnerIcon from "@/icons/SpinnerIcon.vue";

const props = withDefaults(defineProps<{
  catId: number
  sectorId?: number
  thumbnail?: boolean
  oneRowId?: number
}>(),{
  thumbnail: false,
  oneRowId: 0,
})

const ofetch = useFetch()

const bt = ref<BTNode>()
const loading = ref(false)
const error = ref<string | null>(null)

const nid = (window as any).drupalSettings?.matrixNodeId || document.querySelector('article')?.getAttribute('data-history-node-id') as string

const deepestBenefitsCount = computed(() => {
  // @ts-ignore
  return bt.value.benefits.filter(benefit => benefit.deepest).length;
})
const filteredRows = computed(() => {
  if (props.oneRowId) {
    return bt.value?.rows.filter(row => row.id === props.oneRowId)
  }
  // Props value has precedence over select menu
  if (props.sectorId && props.sectorId > 0) {
    return bt.value?.rows.filter(row => row.sector === props.sectorId);
  }
  if (queryParams.sector > 0) {
    return bt.value?.rows.filter(row => row.sector === queryParams.sector)
  }
  return bt.value?.rows;
})

watch(() => props.catId, () => {
  loadBT(nid,queryParams)
})

interface BTQueryParams {
  benefit: number
  sector: number
}

async function loadBT(nid: string, queryParams: BTQueryParams): Promise<void> {
  loading.value = true
  error.value = null
  return ofetch<BTNode>(`/epbapi/bt/${nid}`, {
    params: {
      benefit: props.catId
    }
  })
    .then(response => {
      // Check if response is an error
      if ((response as any).errorMessage) {
        error.value = (response as any).errorMessage
        bt.value = undefined
      } else {
        bt.value = response
      }
    })
    .catch(err => {
      error.value = err.message || 'Failed to load data'
      bt.value = undefined
    })
    .finally(() => loading.value = false)
}

onBeforeMount(() => {
  loadBT(nid,queryParams)
})
</script>

<template>
  <div v-if="loading">
    <div class="bt__loader">
      <SpinnerIcon/>
    </div>
  </div>
  <div v-else-if="error" class="bt__error">
    <p>{{ error }}</p>
  </div>
  <table v-else-if="bt && bt.hierarchy" @click="$emit('open-modal',catId,sectorId)"
  :class="[{'thumbnail': thumbnail},{'onerow': oneRowId > 0}]">
    <thead class="bt__thead" :inert="thumbnail">
    <BenefitsHeaderRecursive :nodes="bt.hierarchy"/>
    </thead>
    <tbody class="bt__body" :inert="thumbnail">
    <tr v-for="qpi in filteredRows">
      <td>{{ qpi.code }}</td>
      <td><a :href="qpi.href">{{ qpi.title }}</a></td>
      <template v-for="i in deepestBenefitsCount" :key="`${catId}-${i}`">
        <BTCell :benefits="qpi.benefitsWithLevel" :index="i - 1"/>
      </template>
    </tr>
    </tbody>
  </table>
</template>

<style scoped>
.bt__loader {
  display: grid;
  place-items: center;
  min-height: 500px;
}

.bt__error {
  padding: 1rem;
  background-color: #fee;
  border: 1px solid #c00;
  color: #900;
  border-radius: 4px;
}

/** Reset some default styles */
table {
  width: auto;
  /** Prevent some weird border transparency whith position: sticky. */
  border-collapse: separate;

}
tr, :deep(tr) {
  th {
    background: transparent;
    color: var(--gray-700);
  }
}
/** End reset */

thead.bt__thead {
  /*position: sticky;*/
  /*top: 56px;*/

  tr, :deep(tr) {

    th {
      text-align: center;
    }

    &:nth-child(odd) {
      background-color: hsl(61.67deg 75% 71.76%);
    }

    &:nth-child(even) {
      background-color: hsl(60deg 73.81% 83.53%);
    }

    &:first-child {
      background-color: hsl(95.45deg 41.51% 79.22%);
      text-transform: uppercase;
    }

    &:last-child {
      th {
        padding: 9px;
        writing-mode: sideways-lr;
        width: 60px;
        background-color: hsl(61.67deg 75% 71.76%);
        text-align: left;
        max-height: 220px;

        a {
          font-weight: 500;
          color: initial;
          text-decoration: none;
        }

        &:hover {
          a {
            text-decoration: underline dotted;
          }
        }
      }
    }
  }
}

tbody.bt__body {
  tr {
    height: 60px;

    td {
      &:nth-child(2) {
        width: 300px;
        text-align: right;
        a {
          color: initial;
          text-decoration: none;
        }
      }
      &:hover {
        &:nth-child(2) {
          a {
            text-decoration: underline dotted;
          }
        }
      }
    }

    &:nth-child(even) {
      td:first-child, td:nth-child(2) {
        background-color: hsl(61.67deg 75% 71.76%);
      }
    }

    &:nth-child(odd) {
      td:first-child, td:nth-child(2) {
        background-color: hsl(60deg 73.81% 83.53%);
      }
    }
  }
}
</style>
